import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import moment from "moment";
import { connect } from "react-redux";
import { connect as formikConnect } from "formik";
import { compose } from "redux";
import { Checkbox, FormControl, FormControlLabel, Typography } from "@material-ui/core";
import {
  ButtonIcon,
  FormikInputTypeAhead,
  SecondaryButton,
  PrimaryButton,
  ButtonSizeOptions,
  FormikInputDatePicker,
  FormikInputText,
  FormikInputCheckbox,
  InputSwitch
} from "@loanbossinc/component-library";

import { downloadFile } from "../../utils/api";

import { putAttachment, postAttachment, deleteAttachment } from "./lenderReportingDashboard.store";
import FileComponent from "./fileComponent";
import { TaskStatus, LrdRoleType } from "./constants";
import { trackLCTaskCompletion, trackFileAttachmentToLCTask } from "lib/mixpanel";

const TaskWrapper = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #dadce0;
  box-sizing: border-box;
  box-shadow: 0px 10px 20px rgba(173, 174, 174, 0.25);
  border-radius: 8px;
  margin-bottom: 43px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const EmptyDiv = styled.div`
  height: 42px;
  width: 386px;
`;

const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 1em;
  border-bottom: 1px solid #dadce0;
`;

const DataColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const LeftSideData = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100px;
`;

const RightSideData = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100px;
`;

const BaseTextComponent = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  color: #000000;
  display: flex;
  flex-direction: row;
`;

const TaskName = styled(BaseTextComponent)`
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 23px;
`;

const DueDate = styled(BaseTextComponent)`
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  display: flex;
  align-items: center;
  color: #5f6368;
`;

const AssignedTo = styled(BaseTextComponent)`
  font-size: 16px;
  line-height: 19px;
  display: flex;
  align-items: center;
  text-align: right;
  color: #000000;
`;

const SubText = styled.div`
  margin-left: 5px;
`;

const IndicatorRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

const Privileged = styled.div`
  width: 133px;
  height: 20px;
  background: #c40a0a;
  border-radius: 4px;
  font-family: Work Sans;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
  text-transform: uppercase;
  color: #ffffff;
  margin-left: 20px;
  margin-right: 6px;
`;

const AttachFileLink = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #005aa9;
  text-decoration: none;
  height: 25px;
  width: fit-content;
  padding: 1em;
  padding-left: 2em;

  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: #005aa9a1;
  }
`;

const Label = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #5f6368;
`;

const DeleteTaskButton = styled(SecondaryButton)`
  && {
    background: #ffffff;
    border: 1px solid #c40a0a;
    box-sizing: border-box;
    border-radius: 4px;
    color: #c40a0a;
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  && {
    margin-left: 16px;
  }
`;

const ComponentWrapper = styled.div`
  margin-bottom: 20px;
`;

const FileItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid #dadce0;
  border-width: 100%;
  font-family: "Work Sans";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  padding: 1em;
  align-items: center;
`;

const IconWrapper = styled.div`
  padding-top: 3px;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledCheckbox = styled(Checkbox)`
  height: 25px !important;
  & .Mui-checked {
    color: #2c98f0 !important;
  }
`;

const StyledDeliverableSwitch = styled(InputSwitch)`
  & .Mui-checked {
    color: #2c98f0 !important;
  }
`;

const TaskCompleteWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Unassigned = styled.div`
  font-family: Work Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  display: flex;
  align-items: center;
  color: #c40a0a;
`;

const mapContactsHelper = ({ contact, contactId }) => ({
  name: contact?.name ? contact?.name : `${contact?.firstName} ${contact?.lastName}`,
  value: contactId,
  id: contactId
});

const TaskComponent = props => {
  const {
    isPrivilegedUser,
    permissionedUsersByCompany = [],
    formik,
    task,
    tasksList,
    configItems,
    openNotes,
    handleSubmit,
    handleCancel,
    onDelete,
    onStatusChange
  } = props;

  const [assignees, setAssignees] = useState([{ name: "", id: "" }]);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [taskAssigneeFullName, setTaskAssigneeFullName] = useState("");
  const currentTaskAssignee = task?.taskAssignees[0];
  const isPrivilegedTask = task?.isPrivileged;
  const hideAttachments = isPrivilegedTask && !isPrivilegedUser;

  useEffect(() => {
    const allPermissionedUsers = permissionedUsersByCompany.map(mapContactsHelper);

    const privilegedUsers = permissionedUsersByCompany
      .filter(({ isPrivileged, lenderReportingRoleTypeId }) => isPrivileged || lenderReportingRoleTypeId === LrdRoleType.ADMIN)
      .map(mapContactsHelper);

    isPrivilegedTask ? setAssignees(privilegedUsers) : setAssignees(allPermissionedUsers);
  }, [permissionedUsersByCompany]);

  useEffect(() => {
    if (currentTaskAssignee && currentTaskAssignee.contact) {
      setTaskAssigneeFullName(`${currentTaskAssignee.contact.firstName} ${currentTaskAssignee.contact.lastName}`);
    }
  }, [currentTaskAssignee]);

  const handleDelete = () => {
    onDelete(task.id, formik);
  };

  const handleFileDelete = fileId => {
    props.deleteAttachment({ fileId, taskId: task.id });
    // update status of "completed" task to "in progress" if no attachments are present
    if (task.status.id === TaskStatus.COMPLETED && task.files?.length === 1) {
      onStatusChange(task, formik, TaskStatus.IN_PROGRESS);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(!isEditing);
    handleCancel();
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
    setAttachmentOpen(false);
  };

  const toggleAttachments = () => {
    setAttachmentOpen(!attachmentOpen);
  };

  const handleDownload = fileId => downloadFile(`files/${fileId}`);

  const handleCompleteToggle = e => {
    const { checked } = e.target;
    const statusId = checked ? TaskStatus.COMPLETED : TaskStatus.IN_PROGRESS;
    onStatusChange(task, formik, statusId);
    if (checked) {
      trackLCTaskCompletion();
    }
  };

  const saveTask = () => {
    setIsEditing(!isEditing);
    handleSubmit();
  };

  const determineFormikValueIndex = () => tasksList.findIndex(t => task.id === t.id);

  const getFileAttachmentText = () => (task.files.length === 1 ? `${task.files.length} file attached` : `${task.files.length} files attached`);

  if (!task || !configItems) {
    return null;
  }

  return (
    <TaskWrapper>
      {!isEditing && (
        <DataRow>
          <LeftSideData>
            <TaskName>{task.taskName}</TaskName>
            <DueDate>
              Due {task.dueDate ? moment.utc(task.dueDate).format("MMMM DD, YYYY") : "--"}
            </DueDate>
            {(!task.taskAssignees[0] || !task.taskAssignees[0].contactId) && <Unassigned>Unassigned</Unassigned>}
            {task.taskAssignees[0] && task.taskAssignees[0]?.contactId && (
              <AssignedTo>
                Assigned to
                {" "}
                <SubText key={`contact-${task.taskAssignees[0]?.contactId}`}>{taskAssigneeFullName}</SubText>
              </AssignedTo>
            )}
          </LeftSideData>
          <RightSideData>
            <IndicatorRow>
              {isPrivilegedTask && <Privileged>Privileged info</Privileged>}
              <span style={{ marginLeft: "10px" }}>
                <ButtonIcon onClick={() => openNotes(task)} iconName={"speaker_notes"} />
              </span>
              <span style={{ marginLeft: "10px" }}>
                <ButtonIcon onClick={toggleEditing} iconName={"settings"} />
              </span>
            </IndicatorRow>
            <TaskCompleteWrapper>
              <FormControl>
                <FormControlLabel
                  control={<StyledCheckbox name="task-complete" />}
                  id="task-complete-status"
                  label={
                    <Typography style={{ fontSize: 16, fontFamily: "Work Sans", color: "initial" }}>
                      Task complete
                    </Typography>
                  }
                  labelPlacement={"start"}
                  style={{ marginRight: "-8px" }}
                  checked={task.statusId === TaskStatus.COMPLETED}
                  onChange={handleCompleteToggle}
                />
              </FormControl>
            </TaskCompleteWrapper>
          </RightSideData>
        </DataRow>
      )}

      {isEditing && (
        <DataRow>
          <div>
            <ComponentWrapper>
              <TaskName>Edit Task</TaskName>
            </ComponentWrapper>
            <ComponentWrapper>
              <FormikInputText name={`tasks[${determineFormikValueIndex()}].taskName`} label="Task Name" variant={"filled"} style={{ width: "435px" }} />
            </ComponentWrapper>
            <ComponentWrapper>
              <FormikInputTypeAhead
                id="taskAssigneesContactId"
                name={`tasks[${determineFormikValueIndex()}].taskAssignees[0].contactId`}
                label="Assigned To"
                options={assignees}
                displayWith={option => option?.name}
                setValueWith={option => option?.id}
                className="input"
                variant={"filled"}
                style={{ width: "435px" }}
              />
            </ComponentWrapper>
          </div>
          <div>
            <ComponentWrapper style={{ marginBottom: "9px", paddingTop: "20px" }}>
              <Label>Due on</Label>
            </ComponentWrapper>
            <ComponentWrapper>
              <FormikInputDatePicker id="due-date" name={`tasks[${determineFormikValueIndex()}].dueDate`} label="Due on" variant="filled" />
            </ComponentWrapper>
            <ComponentWrapper>
              {isPrivilegedUser ? (
                <FormikInputCheckbox name={`tasks[${determineFormikValueIndex()}].isPrivileged`} label="This task contains privileged information" />
              ) : (
                <EmptyDiv />
              )}
            </ComponentWrapper>
          </div>
        </DataRow>
      )}
      {!isEditing && (
        <DataColumn>
          <div>
            {hideAttachments ?
              <FileItem>
                <div style={{ width: "400px" }}>
                  {getFileAttachmentText()}
                </div>
              </FileItem> :
              task.files &&
              task.files.length > 0 &&
              task.files.map((file) => (
                <FileItem key={file.id}>
                  <div style={{ width: "400px" }}>{file.name}</div>
                  <StyledButtonWrapper>
                    <IconWrapper>
                      <ButtonIcon onClick={() => handleDownload(file.id)} iconName={"file_download"} style={{ marginLeft: "23px" }} />
                      <ButtonIcon onClick={() => handleFileDelete(file.id)} iconName={"close"} style={{ marginLeft: "23px" }} />
                    </IconWrapper>
                  </StyledButtonWrapper>
                </FileItem>
              ))}
          </div>
          {!attachmentOpen && <AttachFileLink onClick={toggleAttachments}>Attach a file to this task</AttachFileLink>}
          {attachmentOpen && (
            <FileComponent
              cancel={toggleAttachments}
              onDrop={files => {
                if (files) {
                  if (files.length > 0) {
                    for (const file of files) {
                      props.postAttachment({ modelName: "task", taskId: task.id, modelId: task.id, subKey: task.id, file });
                    }
                  } else {
                    props.postAttachment({ modelName: "task", taskId: task.id, modelId: task.id, subKey: task.id, file: files });
                  }
                  trackFileAttachmentToLCTask();
                }
              }}
            />
          )}
        </DataColumn>
      )}

      {!attachmentOpen && isEditing && (
        <DataRow>
          <div>
            <DeleteTaskButton label="Delete Task" size={ButtonSizeOptions.smFluid} onClick={handleDelete} />
          </div>
          <div>
            <SecondaryButton label="Cancel" size={ButtonSizeOptions.smFluid} onClick={handleCancelEdit} />
            <StyledPrimaryButton label={"Save Changes"} size={ButtonSizeOptions.smFluid} onClick={saveTask} />
          </div>
        </DataRow>
      )}
    </TaskWrapper>
  );
};

const mapStateToProps = ({ lenderReportingDashboard, authentication }) => ({
  ...lenderReportingDashboard,
  authentication
});

export default compose(
  formikConnect,
  connect(mapStateToProps, {
    putAttachment,
    postAttachment,
    deleteAttachment
  })
)(TaskComponent);
